import { auth, db } from '@services/firebase/config';
import {
  createUserWithEmailAndPassword,
  GoogleAuthProvider,
  sendPasswordResetEmail,
  signInWithEmailAndPassword,
  signInWithPopup,
  signOut,
  updatePassword,
} from 'firebase/auth';
import { doc, getDoc, setDoc, updateDoc } from 'firebase/firestore';
import { createContext, ReactNode, useEffect, useState } from 'react';

export interface AuthContextValue {
  user: any;
  loading: boolean;
  signup: (email: string, password: string, profileData?: any) => Promise<any>;
  login: (email: string, password: string) => Promise<any>;
  loginWithGoogle: () => Promise<any>;
  logout: () => Promise<void>;
  resetPassword: (email: string) => Promise<void>;
  updateUserProfile: (userData: any) => Promise<void>;
  updateUserPassword: (newPassword: string) => Promise<void>;
  sendPasswordResetEmail: typeof sendPasswordResetEmail;
}

export const AuthContext = createContext<AuthContextValue | undefined>(
  undefined
);

interface AuthProviderProps {
  children: ReactNode;
}

export function AuthProvider({ children }: AuthProviderProps) {
  const [user, setUser] = useState<any>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const unsubscribe = auth.onAuthStateChanged(async (authUser) => {
      if (authUser) {
        setUser({
          uid: authUser.uid,
          email: authUser.email,
          photoURL: authUser.photoURL,
          providerData: authUser.providerData,
        });
      } else {
        setUser(null);
      }
      setLoading(false);
    });

    return () => unsubscribe();
  }, []);

  const signup = async (
    email: string,
    password: string,
    profileData: any = {}
  ) => {
    const userCredential = await createUserWithEmailAndPassword(
      auth,
      email,
      password
    );
    await setDoc(doc(db, 'users', userCredential.user.uid), {
      email,
      ...profileData,
      createdAt: new Date().toISOString(),
    });
    return userCredential;
  };

  const login = (email: string, password: string) => {
    return signInWithEmailAndPassword(auth, email, password);
  };

  const loginWithGoogle = async () => {
    const provider = new GoogleAuthProvider();
    const result = await signInWithPopup(auth, provider);
    const userDoc = await getDoc(doc(db, 'users', result.user.uid));

    if (!userDoc.exists()) {
      await setDoc(doc(db, 'users', result.user.uid), {
        email: result.user.email,
        photoURL: result.user.photoURL,
        createdAt: new Date().toISOString(),
      });
    }
    return result;
  };

  const logout = () => {
    return signOut(auth);
  };

  const resetPasswordFn = (email: string) => {
    return sendPasswordResetEmail(auth, email);
  };

  const updateUserPasswordFn = async (newPassword: string) => {
    if (!auth.currentUser) throw new Error('No hay usuario autenticado');
    return updatePassword(auth.currentUser, newPassword);
  };

  const updateUserProfile = async (userData: any) => {
    if (!user) throw new Error('No hay usuario autenticado');

    const { password, ...profileData } = userData;

    // Si hay una nueva contrasena, actualizarla primero
    if (password) {
      await updateUserPasswordFn(password);
    }

    // Actualizar el resto de los datos del perfil
    if (Object.keys(profileData).length > 0) {
      const userRef = doc(db, 'users', user.uid);
      await updateDoc(userRef, {
        ...profileData,
        updatedAt: new Date().toISOString(),
      });

      const updatedUser = { ...user, ...profileData };
      setUser(updatedUser);
    }
  };

  const value: AuthContextValue = {
    user,
    loading,
    signup,
    login,
    loginWithGoogle,
    logout,
    resetPassword: resetPasswordFn,
    updateUserProfile,
    updateUserPassword: updateUserPasswordFn,
    sendPasswordResetEmail,
  };

  return (
    <AuthContext.Provider value={value}>
      {!loading && children}
    </AuthContext.Provider>
  );
}
